import tw from 'twin.macro'
import React, { useContext } from 'react'
import { ContractorsContext } from '../contractors/Contractors.context'
import { ContractorStatusSelect } from '../shared/ContractorStatusSelect'
import { ContractorRecollectionSelect } from '../shared/ContractorRecollectionSelect'
import { ContractorReActivationSelect } from '../shared/ContractorReActivationSelect'
import { ContractorExpirationSelect } from '../shared/ContractorExpirationSelect'
import { ContractorVehicleTypeSelect } from '../shared/ContractorVehicleTypeSelect'
import { ContractorDateOnboardedSelect } from '../shared/ContractorDateOnboardedSelect'
import { ContractorState } from 'src/constants/contractor/enums'
import { ContractorsTableContext } from '../contractors-table/ContractorsTable.context'
import { TableTagsSelect } from 'src/components/shared/table/TableTagsSelect'
import { TableAssignedTemplateSelect } from 'src/components/shared/table/TableAssignedTemplateSelect'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { GridColumnsSelect } from 'src/components/shared/grid/grid-columns-select/GridColumnsSelect'
import { FilterColumnGuard } from 'src/components/shared/FilterColumnGuard'

interface Props {
  selectedContractorState: ContractorState
}

export const ContractorsHeader = ({ selectedContractorState }: Props) => {
  const { setSearchQuery, searchQuery } = useContext(ContractorsContext)
  const {
    tagsFilter,
    setTagsFilter,
    templateFilter,
    setTemplateFilter,
    vehicleTypeFilter,
    setVehicleTypeFilter,
    statusFilter,
    setStatusFilter,
    expirationIndicatorFilter,
    setExpirationIndicatorFilter,
    recollectionFilter,
    setRecollectionFilter,
    reActivationFilter,
    setReActivationFilter,
    dateOnboardedRange,
    setDateOnboardedRange,
    columnsConfig,
    columnVisibilityModel,
    pinnedColumns,
    lockedColumns,
    onToggleColumnVisibility,
    onColumnVisibilityModelChange,
    onPinColumn,
    onUnpinColumn,
  } = useContext(ContractorsTableContext)
  const { hasPermission } = useCheckPermissions()

  const isColumnHidden = (field: string) => {
    return columnVisibilityModel[field] === false
  }

  return (
    <div
      css={tw`flex flex-row flex-wrap items-center gap-2 px-4 py-3 bg-white border-0 border-b border-t border-solid border-[#EDEDED]`}
    >
      {columnsConfig.length > 0 && (
        <GridColumnsSelect
          columnsConfig={columnsConfig}
          columnVisibilityModel={columnVisibilityModel}
          pinnedColumns={pinnedColumns}
          lockedColumns={lockedColumns}
          onToggleColumnVisibility={onToggleColumnVisibility}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          onPinColumn={onPinColumn}
          onUnpinColumn={onUnpinColumn}
        />
      )}
      <TableSearch
        searchQuery={searchQuery}
        onChangeSearchQuery={setSearchQuery}
        width={284}
        inputDataTestId="contractors-list-main-search-input"
        clearButtonDataTestId="contractors-list-main-search-clear-btn"
      />
      {hasPermission(UserPermission.TagsView) && (
        <FilterColumnGuard
          hidden={isColumnHidden('tags')}
          columnName="Tag(s)"
          hasValue={tagsFilter.length > 0}
          onClear={() => {
            setTagsFilter([])
          }}
        >
          <TableTagsSelect
            selectedTags={tagsFilter}
            setSelectedTags={setTagsFilter}
            triggerDataTestId="contractors-list-main-tags-menu-btn"
            clearAllDataTestId="contractors-list-main-tags-clear-all"
            manageOptionsDataTestId="contractors-list-main-tags-manage-options"
          />
        </FilterColumnGuard>
      )}
      <FilterColumnGuard
        hidden={isColumnHidden('onboardingFlow')}
        columnName="Assigned Template"
        hasValue={templateFilter.length > 0}
        onClear={() => {
          setTemplateFilter([])
        }}
      >
        <TableAssignedTemplateSelect
          selectedTemplateIds={templateFilter}
          setSelectedTemplateIds={setTemplateFilter}
          triggerDataTestId="contractors-list-main-template-menu-btn"
          clearAllDataTestId="contractors-list-main-template-clear-all"
          searchInputDataTestId="contractors-list-main-template-search-input"
        />
      </FilterColumnGuard>
      <FilterColumnGuard
        hidden={isColumnHidden('vehicleType')}
        columnName="Vehicle Type"
        hasValue={vehicleTypeFilter.length > 0}
        onClear={() => {
          setVehicleTypeFilter([])
        }}
      >
        <ContractorVehicleTypeSelect />
      </FilterColumnGuard>
      {selectedContractorState === ContractorState.Recollection && (
        <FilterColumnGuard
          hidden={isColumnHidden('status')}
          columnName="Status"
          hasValue={statusFilter.length > 0}
          onClear={() => {
            setStatusFilter([])
          }}
        >
          <ContractorStatusSelect />
        </FilterColumnGuard>
      )}
      {selectedContractorState === ContractorState.Active && (
        <FilterColumnGuard
          hidden={isColumnHidden('expiresInDays')}
          columnName="Expires In"
          hasValue={expirationIndicatorFilter.length > 0}
          onClear={() => {
            setExpirationIndicatorFilter([])
          }}
        >
          <ContractorExpirationSelect />
        </FilterColumnGuard>
      )}
      {selectedContractorState === ContractorState.Active && (
        <FilterColumnGuard
          hidden={isColumnHidden('recollections')}
          columnName="Recollection"
          hasValue={recollectionFilter.length > 0}
          onClear={() => {
            setRecollectionFilter([])
          }}
        >
          <ContractorRecollectionSelect />
        </FilterColumnGuard>
      )}
      {selectedContractorState === ContractorState.Active && (
        <FilterColumnGuard
          hidden={isColumnHidden('dateOnboarded')}
          columnName="Date onboarded"
          hasValue={Boolean(dateOnboardedRange[0] || dateOnboardedRange[1])}
          onClear={() => {
            setDateOnboardedRange([null, null])
          }}
        >
          <ContractorDateOnboardedSelect />
        </FilterColumnGuard>
      )}
      <FilterColumnGuard
        hidden={isColumnHidden('autoActivationEnabled')}
        columnName="Re-Activation"
        hasValue={reActivationFilter.length > 0}
        onClear={() => {
          setReActivationFilter([])
        }}
      >
        <ContractorReActivationSelect />
      </FilterColumnGuard>
    </div>
  )
}
